import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { DialogService } from '../../shared/components/dialog/dialog.service';
import { TranslateModule } from '@ngx-translate/core';
import { ApiService } from '../../_services/api.service';
import { AuthService } from '../../_services/auth.service';
import { isOrganisationTemplate, Template } from '../../_interfaces/template';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner/loading-spinner.component';
import { TemplatesStepDialogComponent } from './templates-step-dialog.component';
import { TemplateSelector } from '../../shared/components/template-selector/template-selector.component';

@Component({
  selector: 'app-templates-step',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    TranslateModule,
    LoadingSpinnerComponent,
    TemplateSelector,
  ],
  template: `
    <div class="flex flex-col flex-1 min-h-0">
      <div class="flex-1 overflow-hidden min-h-0 flex flex-col">

        <h1
          class="max-md:hidden headline-small text-primary mb-2 text-center shrink-0"
        >
          {{ 'All blueprints' | translate }}
        </h1>

        @if (showBlueprints) {
          <p class="body-large text-on-surface-variant mb-2 shrink-0">
            {{ 'Choose between the AI blueprints set up for your organisation, or your library of AI blueprints' | translate }}
          </p>
        }

        <div class="flex-1 min-h-0 flex flex-col overflow-hidden">
          <app-template-selector
            class="flex-1 min-h-0"
            [loading]="templatesLoading"
            [templates]="templates"
            [selectedTemplates]="originallySelectedTemplates"
            (selectionChange)="updateSelectionChange($event)"
          >
          </app-template-selector>
        </div>

      </div>

      <div class="flex flex-col items-center gap-2 pt-4 shrink-0">
        <button
          mat-flat-button
          color="primary"
          class="w-full"
          [disabled]="loading"
          (click)="onContinue()"
        >
          <app-loading-spinner [loading]="loading" [diameter]="20">
            {{ 'Confirm selection' | translate }}
          </app-loading-spinner>
        </button>
      </div>
    </div>
  `,
  styles: [`
    :host { display: flex; flex-direction: column; flex: 1; min-height: 0; }
  `],
})
export class TemplatesStepComponent implements OnInit {
  @Input() loading = false;
  @Output() submitTemplates = new EventEmitter<{ id: number; type: string }[]>();

  templates: Template[] = [];
  selectedTemplates: Template[] = [];
  originallySelectedTemplates: Template[] = [];
  defaultTemplateId?: number;
  templatesLoading = true;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private dialog: DialogService,
  ) {}

  get showBlueprints(): boolean {
    return this.authService.isAiBlueprintsEnabled;
  }

  async ngOnInit(): Promise<void> {
    this.defaultTemplateId = this.authService.getUser()?.settings?.default_template_id;
    try {
      this.templates = await this.apiService.getCopilotTemplates();
      this.selectedTemplates = this.getDefaultTemplates();
      this.originallySelectedTemplates = this.selectedTemplates;
    } catch {
      this.templates = [];
    } finally {
      this.templatesLoading = false;
    }
  }

  private getDefaultTemplates(): Template[] {
    const settings = this.authService.getUser()?.settings;
    const saved = settings?.copilotTemplates || [];

    if (saved.length) {
      return this.templates.filter(t =>
        saved.some(d => d.id === t.id && d.type === t.type)
      );
    }

    const fallback = this.templates.find(t => t.id === settings?.default_template_id);
    return fallback ? [fallback] : [];
  }

  private matchesTemplate(a: Template, b: Template): boolean {
    return a.id === b.id && a.type === b.type;
  }

  get displayedTemplates(): Template[] {
    const standardTemplates = this.templates.filter(t => isOrganisationTemplate(t.type));
    const selectedCustomTemplates = this.authService.isAiBlueprintsEnabled
      ? this.selectedTemplates.filter(t => t.type === 'user')
      : [];

    const defaultTemplateIndex = standardTemplates.findIndex(t => t.id === this.defaultTemplateId);
    const defaultTemplate = defaultTemplateIndex !== -1
      ? standardTemplates.splice(defaultTemplateIndex, 1)
      : [];

    return [...defaultTemplate, ...standardTemplates, ...selectedCustomTemplates];
  }

  isSelected(template: Template): boolean {
    return this.selectedTemplates.some(t => this.matchesTemplate(t, template));
  }

  updateSelectionChange(templates: Template[]): void {
    this.selectedTemplates = templates;
  }

  openTemplateDialog(): void {
    const dialogRef = this.dialog.open(TemplatesStepDialogComponent, {
      size: 'mdPlus',
      data: {
        templates: this.templates,
        selectedTemplates: this.selectedTemplates.map(t => ({ id: t.id, type: t.type })),
      },
    });

    dialogRef.afterClosed().subscribe((result?: Template[]) => {
      if (result) {
        this.selectedTemplates = result;
      }
    });
  }

  onContinue(): void {
    const mapped = this.selectedTemplates.map(t => ({ id: t.id, type: t.type }));
    this.submitTemplates.emit(mapped);
  }
}
